import type {
  CustomAgentInvocation,
  CustomAgentResultItem
} from '@axilon-apps/domains-ontology-ui-state'

import type { ToolCallRowEntry } from '../../components/ToolTrace/ToolCallRow'
import {
  readCallDurationMs,
  readCallStartedAt,
  readCallTurnTiming
} from '../../components/ToolTrace/toolCallTiming'
import { invocationStepMs } from './run-timing'
import { readInvocationBatchId } from './run-invocation-waves'

export function invocationsToToolCallEntries(
  invocations: CustomAgentInvocation[],
  firstPreviousAt: string
): ToolCallRowEntry[] {
  return invocations.map((inv, i) => {
    const { input, output } = splitInvocationPayload(inv.payload)
    const durationMs =
      readCallDurationMs(inv.payload) ??
      invocationStepMs(inv.created_at, i === 0 ? firstPreviousAt : invocations[i - 1].created_at)
    const startedAt = readCallStartedAt(inv.payload)
    const turnTiming = readCallTurnTiming(inv.payload)
    const batchId = readInvocationBatchId(inv)
    return {
      id: inv.id,
      name: inv.tool_name,
      input,
      output,
      parentStepIndex: 0,
      agentKind: '',
      depth: 0,
      status: invocationStatus(output),
      durationMs,
      ...(startedAt !== undefined ? { startedAt } : {}),
      ...(turnTiming !== undefined ? { turnTiming } : {}),
      ...(batchId !== undefined ? { batchId } : {})
    }
  })
}

export function resultItemDisplayTitle(item: CustomAgentResultItem): string {
  const title = readResultString(item.result, 'title')
  if (title) return title
  const name = readResultString(item.result, 'name')
  if (name) return name
  const headline = readResultString(item.result, 'headline')
  if (headline) return headline
  const failureMode = readResultString(item.result, 'failure_mode')
  if (failureMode) return failureMode
  const stage = readResultString(item.result, 'stage')
  if (stage) return stage
  const station = readResultString(item.result, 'station')
  if (station) return station
  const description = readResultString(item.result, 'description')
  if (description) return description
  if (item.type === 'root_causes') return `Root cause ${item.position + 1}`
  if (item.type === 'action_items') return `Action ${item.position + 1}`
  if (item.type === 'notable_events') return `Event ${item.position + 1}`
  if (item.type === 'predictions') return `Prediction ${item.position + 1}`
  return 'finding'
}

function readResultString(result: Record<string, unknown>, key: string): string | null {
  const value = result[key]
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function invocationStatus(output: unknown): 'completed' | 'error' {
  if (output !== null && typeof output === 'object' && 'error' in output) {
    const error = (output as { error: unknown }).error
    if (typeof error === 'string' && error.length > 0) return 'error'
  }
  return 'completed'
}

function splitInvocationPayload(payload: Record<string, unknown>): {
  input: unknown
  output: unknown
} {
  if (payload && typeof payload === 'object' && 'input' in payload && 'output' in payload) {
    return { input: payload.input, output: payload.output }
  }
  return { input: null, output: payload }
}
